import { useTranslation } from 'next-i18next'
import ReactMarkdown from 'react-markdown'
import CheckCircle from '../icons/CheckCircle'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { useQuery } from '@apollo/client'
import { GET_APPLICATIONS } from '../../services/graphql/queries/getApplications'
import { useMemo } from 'react'
import useTranslatedRoutes from '../../services/translatedRoutes'
import { useRouter } from 'next/router'
import Link from 'next/link'

export default function PluginAbout ({ plugin, description }) {
  const { data } = useQuery(GET_APPLICATIONS)
  const { t } = useTranslation(['plugins', 'routes', 'categories'])
  const router = useRouter()
  const language = router.locale.toUpperCase()
  const { getCategoryUrl } = useTranslatedRoutes(language)
  const compatibilitiesVersions = useMemo(() => {
    const versions = {}

    if (!data) {
      return versions
    }

    plugin.compatibilities.forEach(compatibility => {
      const application = data.applications.find(application => application.name === compatibility.software)
      const version = application.versions.find(version => version.name === compatibility.version)

      if (!versions[compatibility.software]) {
        versions[compatibility.software] = []
      }

      versions[compatibility.software].push(version?.id)
    })

    return versions
  })

  return (
    <>
      <div className="col-12 col-lg-7">
        <div className="row justify-content-between">
          <div className="col-12">
            <h3 className="fs-4 text-dark">
              {t('detail.about_the_plugin')}
            </h3>
            {
              <p>
                {plugin.descriptions.length > 0 ? description?.intro : t('detail.no_description')}
              </p>
            }
            {
              (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  linkTarget="_blank"
                >
                  {plugin.descriptions.length > 0 ? description?.description : t('detail.no_description')}
                </ReactMarkdown>
              )
            }
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-4">
        <hr className="mb-4 d-lg-none" />
        <h3 className="text-dark fs-4 mb-3">Details</h3>
        <div className="row g-3 g-md-5 g-lg-4">
          <div className="col-12 col-sm-6 col-md-12">
            <h3 className="text-dark fs-5">{t('detail.compatibility')}</h3>
            <ul className="p-0 m-0 list-unstyled">
              {
                Object.keys(compatibilitiesVersions).map((compatibilityName, key) => (
                  <div key={`${compatibilityName}-${key}`}>
                    {key !== 0 && (<hr />)}
                    <li className="mt-3">
                      <span className="d-flex align-items-center">
                        <CheckCircle variant="primary" />
                        <span className="ms-2 text-dark">{t(compatibilityName, { ns: 'applications' })}</span>
                      </span>
                      <ul className="list-unstyled">
                        <li>
                          <span className="fs-5">
                            {
                              compatibilitiesVersions[compatibilityName].join(', ')
                            }
                          </span>
                        </li>
                      </ul>
                    </li>
                  </div>
                ))
              }
            </ul>
          </div>
          <div className="col-12 col-sm-6">
            <h3 className="text-dark fs-5">{t('detail.version')}</h3>
            {plugin.version}
          </div>
          <div className="col-12 col-sm-6">
            <h3 className="text-dark fs-5">{t('detail.last_release')}</h3>
            {plugin.last_updated}
          </div>
          <div className="col-12">
            <h3 className="text-dark fs-5">{t('detail.language')}</h3>
            {
              plugin.languages
                .map(language => (
                  t(language.language, { ns: 'languages' })
                ))
                .join(', ')
            }
          </div>
          <div className="col-12 col-sm-6 col-md-12">
            <h3 className="text-dark fs-5">{t('detail.categories')}</h3>
            <div className="mt-3 d-flex flex-wrap gap-2">
              {
                plugin.categories.map((category) => {
                  category.name = category.category
                  return category
                }).map(category => (
                  <Link
                    href={getCategoryUrl(category)}
                    key={category.id}
                  >
                    <a
                      className="rounded-pill badge bg-primary text-light text-decoration-none"
                    >
                      {t(category.category, { ns: 'categories' })}
                    </a>
                  </Link>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
